import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import { type QuestKind, allQuests, npcMarker } from '../../game/quests';
import type { ZoneLayout } from '../../zones/types';
import { openObjectives, questStatus } from './logic';

/**
 * What the map (M) shows: icons for a zone layout from the character's state
 * (waystones, exits, dungeons, NPCs with quest marks, active objectives,
 * hinted collectibles) and the world overview. Pure — the panel adds the
 * player arrow and draws it.
 */

export type MapIconKind =
  | 'waystone'
  | 'exit'
  | 'dock'
  | 'dungeon'
  | 'npc'
  | 'npcQuest'
  | 'npcTurnIn'
  | 'objective'
  | 'seed';

export interface MapIcon {
  kind: MapIconKind;
  x: number;
  z: number;
  label: string;
  sub?: string;
  /** Objective area radius (world metres). */
  r?: number;
  /** Waystone attuned / portal open. */
  on?: boolean;
  questId?: string;
  questKind?: QuestKind;
  /** Interactable / portal / NPC id. */
  id?: string;
}

export interface MapSources {
  npcName: (id: string) => string;
  npcRole: (id: string) => string;
  zoneName: (id: string) => string;
  /** Collectible-hint flags: flag → zone. */
  hints: { flag: string; zone: string; kind: 'seed' }[];
  /** Ids of interactables still present in the live zone (defaults to all). */
  present?: Set<string>;
  /** Zone an NPC stands in (marks the exit toward a turn-in elsewhere). */
  npcZone?: (id: string) => string | null;
}

/** Objectives covering a whole zone ("arrive in …") get no area marker. */
const WIDE = 60;

export function zoneMapIcons(
  layout: ZoneLayout,
  c: CharacterSave,
  a: AccountSave,
  src: MapSources,
): MapIcon[] {
  const out: MapIcon[] = [];
  for (const it of layout.interactables) {
    if (it.kind === 'waystone') {
      out.push({
        kind: 'waystone',
        x: it.x,
        z: it.z,
        label: String(it.data?.name ?? 'Waystone'),
        sub: c.waystones.includes(it.id) ? 'Attuned waystone' : 'Waystone — touch to attune',
        on: c.waystones.includes(it.id),
        id: it.id,
      });
    }
  }
  for (const p of layout.portals) {
    if (p.requires && !c.flags[p.requires]) continue;
    const locked = !!p.minLevel && c.level < p.minLevel;
    const level = p.minLevel ? `Lv ${p.minLevel}+` : undefined;
    if (p.kind === 'dungeon' || p.kind === 'instance') {
      const cleared = !!c.flags[`${p.to}_cleared`];
      out.push({
        kind: 'dungeon',
        x: p.x,
        z: p.z,
        label: p.label,
        sub: [level, cleared ? 'Cleared' : null].filter(Boolean).join(' · ') || undefined,
        on: !locked,
        id: p.id,
      });
    } else {
      out.push({
        kind: p.kind === 'dock' ? 'dock' : 'exit',
        x: p.x,
        z: p.z,
        label: p.label,
        sub: locked ? `Requires level ${p.minLevel}` : level,
        on: !locked,
        id: p.id,
      });
    }
  }
  for (const n of layout.npcs) {
    const mark = npcMarker(c, n.npc);
    out.push({
      kind: mark === '!' ? 'npcQuest' : mark === '?' ? 'npcTurnIn' : 'npc',
      x: n.x,
      z: n.z,
      label: src.npcName(n.npc),
      sub: src.npcRole(n.npc),
      id: n.npc,
    });
  }
  // Objectives here; objectives (or turn-ins) elsewhere mark the exit that leads there.
  const exitTo = (zone: string) =>
    layout.portals.find((p) => p.to === zone && (!p.requires || c.flags[p.requires]));
  for (const q of allQuests()) {
    if (!c.quests.active[q.id]) continue;
    const away = new Set<string>();
    if (questStatus(c, q) === 'ready') {
      const z = src.npcZone?.(q.turnIn);
      if (z && z !== layout.id) away.add(`${z}|Report to ${src.npcName(q.turnIn)}`);
    }
    for (const o of openObjectives(c, q)) {
      if (!o.zone || !o.pos) continue;
      if (o.zone !== layout.id) away.add(`${o.zone}|${o.text}`);
      else if ((o.radius ?? 0) < WIDE)
        out.push({
          kind: 'objective',
          x: o.pos.x,
          z: o.pos.z,
          r: o.radius,
          label: o.text,
          sub: q.name,
          questId: q.id,
          questKind: q.kind,
        });
    }
    for (const key of away) {
      const [zone = '', text = ''] = key.split('|');
      const p = exitTo(zone);
      if (p)
        out.push({
          kind: 'objective',
          x: p.x,
          z: p.z,
          r: 5,
          label: text,
          sub: `${q.name} · ${src.zoneName(zone)}`,
          questId: q.id,
          questKind: q.kind,
        });
    }
  }
  const hinted = src.hints.some(
    (h) => h.zone === layout.id && h.kind === 'seed' && !!c.flags[h.flag],
  );
  if (hinted) {
    for (const it of layout.interactables) {
      if (it.kind !== 'seed' || a.collectibles.seeds.includes(it.id)) continue;
      if (src.present && !src.present.has(it.id)) continue;
      out.push({
        kind: 'seed',
        x: it.x,
        z: it.z,
        label: 'Glimmerseed',
        sub: 'Revealed by a quest',
        id: it.id,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------- world overview

export interface WorldZone {
  id: string;
  name: string;
  subtitle?: string;
  kind: string;
  levelRange: [number, number];
  waystones: { id: string; name: string; attuned: boolean }[];
  dungeons: { id: string; name: string; minLevel: number; cleared: boolean }[];
  /** Active quests with work in this zone, the main one first. */
  quests: { id: string; name: string; kind: QuestKind; ready: boolean }[];
  /** Side quests offered here that the character could take now. */
  available: number;
  seeds: { have: number; total: number };
}

export function worldZone(
  layout: ZoneLayout,
  c: CharacterSave,
  a: AccountSave,
  dungeonLayout: (id: string) => ZoneLayout | null,
): WorldZone {
  const quests: WorldZone['quests'] = [];
  let available = 0;
  for (const q of allQuests()) {
    const st = questStatus(c, q);
    if (st === 'available' && q.zone === layout.id && q.kind === 'side') available++;
    if (st !== 'active' && st !== 'ready') continue;
    const here =
      st === 'ready'
        ? layout.npcs.some((n) => n.npc === q.turnIn)
        : openObjectives(c, q).some((o) => o.zone === layout.id);
    if (here) quests.push({ id: q.id, name: q.name, kind: q.kind, ready: st === 'ready' });
  }
  const rank = (k: QuestKind) => (k === 'main' ? 0 : k === 'world' ? 1 : 2);
  quests.sort((x, y) => rank(x.kind) - rank(y.kind));
  const seeds = layout.interactables.filter((i) => i.kind === 'seed');
  const dungeons: WorldZone['dungeons'] = [];
  for (const p of layout.portals) {
    if (p.kind !== 'dungeon') continue;
    const d = dungeonLayout(p.to);
    dungeons.push({
      id: p.to,
      name: d?.name ?? p.label,
      minLevel: p.minLevel ?? d?.levelRange[0] ?? 1,
      cleared: !!c.flags[`${p.to}_cleared`],
    });
  }
  return {
    id: layout.id,
    name: layout.name,
    subtitle: layout.subtitle,
    kind: layout.kind,
    levelRange: layout.levelRange,
    waystones: layout.interactables
      .filter((i) => i.kind === 'waystone')
      .map((w) => ({
        id: w.id,
        name: String(w.data?.name ?? layout.name),
        attuned: c.waystones.includes(w.id),
      })),
    dungeons,
    quests,
    available,
    seeds: {
      have: seeds.filter((s) => a.collectibles.seeds.includes(s.id)).length,
      total: seeds.length,
    },
  };
}

/**
 * Where a quest wants the player next: the zone of its first open objective,
 * or the turn-in NPC's zone when it is ready. Used by "Show on map".
 */
export function questFocus(
  c: CharacterSave,
  questId: string,
  npcZone: (npc: string) => string | null,
): { zone: string; x?: number; z?: number } | null {
  const q = allQuests().find((x) => x.id === questId);
  if (!q) return null;
  const st = questStatus(c, q);
  if (st === 'ready') {
    const z = npcZone(q.turnIn);
    return z ? { zone: z } : null;
  }
  if (st === 'available' || st === 'locked') {
    const z = npcZone(q.giver);
    return z ? { zone: z } : null;
  }
  const o = openObjectives(c, q).find((x) => x.zone);
  if (o?.zone)
    return o.pos && (o.radius ?? 0) < WIDE
      ? { zone: o.zone, x: o.pos.x, z: o.pos.z }
      : { zone: o.zone };
  return { zone: q.zone };
}
